import { useCallback, useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Box,
} from '@chakra-ui/react';
import { PhoneInput, Button, SectionLabel } from '@kula-ai/components';
import { validatePhoneNumber } from '@kula-ai/components';
// import { TimeZoneSelectorV1 } from '@/components/formBuilder/formFields/DateField/components/TimeZoneSelectorV1';
import { KText } from '@/ui-library/components/text';
import { useEditContactMutation } from '@/store/query/contacts';
import { useShowToast } from '@/hooks';
import { COLLECT_PHONE_TIMEZONE_COPY as COPY } from './collectPhoneTimezoneCopy';
import '@/components/formBuilder/formFields/KPhoneField/KPhoneField.css';

export interface CollectPhoneTimezoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * ATS application id. Retained for caller compatibility / resuming the
   * Schedule-with-AI flow on success. The phone number itself is saved on the
   * CONTACT (see handleUpdate) — a phone is a contact-level attribute the
   * Coordinator dials, not an application-level one (ENG-3166).
   */
  applicationId: number | null;
  /** Contact (candidate) for display; time_zone used as initial value */
  contact: {
    id?: number;
    local_phone_number?: string;
    phone_number?: string;
    time_zone?: string;
  } | null;
  /** Called after successful update; can then open Schedule with AI flow */
  onSuccess?: () => void;
}

export function CollectPhoneTimezoneModal({
  isOpen,
  onClose,
  contact,
  onSuccess,
}: CollectPhoneTimezoneModalProps) {
  const toast = useShowToast();
  const [editContact, { isLoading }] = useEditContactMutation();

  const [phoneValue, setPhoneValue] = useState<
    { countryCode: string; dialCode: string; number: string } | undefined
  >(undefined);
  const [timezoneValue, setTimezoneValue] = useState<{
    label: string;
    key: string;
  } | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const phoneWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const nextTimezone = contact?.time_zone
      ? { label: contact.time_zone, key: contact.time_zone }
      : null;
    queueMicrotask(() => {
      setPhoneValue(undefined);
      setTimezoneValue(nextTimezone);
      setPhoneError(null);
    });
  }, [isOpen, contact?.id, contact?.time_zone]);

  const handlePhoneChange = useCallback((value) => {
    setPhoneValue(value);
    setPhoneError(null);
  }, []);

  // const handleTimezoneChange = useCallback(
  //   (value: { label: string; key: string }) => {
  //     setTimezoneValue(value);
  //   },
  //   []
  // );

  const validate = useCallback(() => {
    if (!phoneValue?.number?.trim()) {
      setPhoneError(COPY.phoneRequiredError);
      return false;
    }
    const result = validatePhoneNumber(
      phoneValue.number,
      phoneValue.countryCode
    );
    if (!result.isValid) {
      setPhoneError(result.error || COPY.invalidPhoneError);
      return false;
    }
    // Timezone field hidden for now – no longer required
    // if (!timezoneValue?.key) {
    //   toast({ title: 'Please select a time zone', status: 'error' });
    //   return false;
    // }
    return true;
  }, [phoneValue]);

  const handleUpdate = useCallback(async () => {
    if (!validate() || !contact?.id) return;

    const number = phoneValue?.number?.trim().replace(/\s/g, '');
    if (!number) return;

    try {
      // Phone is a CONTACT-level attribute (the Coordinator dials the contact's
      // number), so write it via the Contact ID API — PUT /contacts/:id — not
      // the application endpoint. Same payload shape as the contact-details
      // editor (CandidatePhone). (ENG-3166)
      const payload: {
        phone_number: string;
        phone_code?: string;
        location?: string;
        time_zone?: string;
      } = { phone_number: number };
      if (phoneValue?.dialCode) payload.phone_code = phoneValue.dialCode;
      if (phoneValue?.countryCode) payload.location = phoneValue.countryCode;
      if (timezoneValue?.key) payload.time_zone = timezoneValue.key;

      await editContact({ payload, candidateId: contact.id }).unwrap();

      toast({ title: COPY.saveSuccess });
      onSuccess?.();
      onClose();
    } catch {
      toast({ title: COPY.saveError, status: 'error' });
    }
  }, [
    validate,
    contact,
    phoneValue,
    timezoneValue,
    editContact,
    toast,
    onSuccess,
    onClose,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontWeight="600" fontSize="16px">
          {COPY.title}
        </ModalHeader>
        <ModalBody>
          <KText size="sm-medium" color="secondary-700" mb="16px">
            {COPY.description}
          </KText>
          <Flex flexDirection="column" gap="16px">
            <Box>
              <SectionLabel>{COPY.phoneLabel}</SectionLabel>
              <Box
                ref={phoneWrapperRef}
                mt="8px"
                className="k-phone-field-wrapper"
              >
                <PhoneInput
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  defaultCountry="US"
                  placeholder={COPY.phonePlaceholder}
                  size="md"
                  error={!!phoneError}
                  name="phone"
                  id="collect-phone-timezone-phone"
                />
              </Box>
              {phoneError && (
                <Box mt="6px" fontSize="12px" color="pastel-1-dark">
                  {phoneError}
                </Box>
              )}
            </Box>
            {/* Timezone field hidden for now – uncomment when needed
            <Box>
              <TimeZoneSelectorV1
                value={timezoneValue}
                onChange={handleTimezoneChange}
                inputProps={{ menuPlacement: 'auto' }}
              />
            </Box>
            */}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap="12px" justifyContent="flex-end">
            <Button variant="secondary" onClick={onClose}>
              {COPY.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdate}
              disabled={isLoading}
              isLoading={isLoading}
            >
              {COPY.update}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
